/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { Link } from "react-router-dom";
import { Cell } from "react-table";
import styled from "styled-components";
import { Button, FlexContainer, Tag } from "@nordcloud/gnui";
import { Role, UserPermissions } from "~/generated/graphql";
import { showRole } from "~/views/settings/UsersAndPermissions/showRole";

type BaseApplicationData = {
  nid: string;
  name: string;
};

const StyledTag = styled(Tag)`
  line-height: 1.2;
  padding: 0.4rem;
`;

type Account = {
  email: string;
  access: {
    role: Role;
    applications: string[];
    applicationsWithNames: {
      nid: string;
      name: string;
    }[];
  };
};

export const UsersAndPermissionsColumns = (
  onEdit: (item: UserPermissions) => void,
  onRemove: (email: string) => void
) => [
  {
    Header: "Email",
    accessor: "email",
    width: "44%",
    disableSortBy: true,
    Cell: ({ row }: Cell<Account>) => <div>{row?.original?.email}</div>,
  },
  {
    Header: "Role",
    accessor: "access.role",
    disableSortBy: true,
    width: "15%",
    Cell: ({ row }: Cell<Account>) => (
      <FlexContainer>
        <StyledTag text={showRole(row?.original?.access?.role)} />
      </FlexContainer>
    ),
  },
  {
    Header: "Permissions",
    accessor: "access.applications",
    width: "35%",
    disableSortBy: true,
    Cell: ({ row }: Cell<Account>) => (
      <FlexContainer wrap="wrap">
        {row?.original?.access?.applicationsWithNames?.map(
          ({ nid, name }: BaseApplicationData) => (
            <Link
              key={nid}
              to={`/applications/${nid}`}
              css={{ "&:hover": { textDecoration: "none" } }}
            >
              <FlexContainer>
                <Tag color="notification" text={name} />
              </FlexContainer>
            </Link>
          )
        )}
      </FlexContainer>
    ),
  },
  {
    Header: "Actions",
    accessor: "actions",
    disableSortBy: true,
    width: "6%",
    Cell: ({ row }: Cell<Account>) => (
      <FlexContainer>
        <Button
          mr="0.5rem"
          size="md"
          severity="low"
          icon="edit"
          onClick={() => {
            onEdit(row?.original);
          }}
        />
        <Button
          size="md"
          severity="low"
          icon="trash"
          onClick={() => onRemove(row?.original?.email)}
        />
      </FlexContainer>
    ),
  },
];
